import "./site/error-beacon";
import { Hailstorm, ABILITY_LABEL, ALL_ABILITIES, LOADOUT_KEY, AbilityKind, AbilityUse } from "./games/hailstorm/hailstorm";
import { recordRun, showXpToast } from "./site/profile";
import { getDailySeed, markDailyComplete, isDailyRun } from "./site/daily";
import "./ui/styles.css";

const PB_KEY = "aether.hailstorm_pb";
const HANDLE_KEY = "aether.handle";

interface HailstormRunStart { run_id: string; seed: number; token: string; game: string; }

async function startHailstormRun(): Promise<HailstormRunStart | null> {
  try {
    const res = await fetch("/api/runs/hailstorm", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({}),
    });
    if (!res.ok) return null;
    return (await res.json()) as HailstormRunStart;
  } catch {
    return null;
  }
}

async function finishHailstormRun(
  runId: string,
  handle: string,
  survivalS: number,
  dodges: { idx: number; distance: number; t?: number }[],
  trace: number[],
  abilitiesUsed: AbilityUse[],
  loadout: AbilityKind[],
  bossKills: { wave_idx: number }[],
): Promise<{
  rank: number;
  total: number;
  percentile: number;
  score: number;
  survival_s?: number;
  dodges_tight?: number;
  waves_cleared?: number;
  bosses_survived?: number;
  abilities_used?: number;
  boss_kills?: number;
  // v5(b) schema-v1 fields the game-over card uses.
  abilities_by_kind?: { dash: number; mine: number; slow: number };
  loadout?: AbilityKind[];
  died_in_wave?: string;
  died_in_wave_idx?: number;
  tight_fraction?: number;
  // v5(c) cadence fields (nullable when legacy clients submit without t).
  time_to_first_dodge_ms?: number | null;
  mean_inter_dodge_ms?: number | null;
  std_inter_dodge_ms?: number | null;
} | null> {
  try {
    const res = await fetch(`/api/runs/hailstorm/${runId}`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        handle, survival_s: survivalS, dodges, trace,
        abilities_used: abilitiesUsed, loadout, boss_kills: bossKills,
        // v5(b): mode field. Hailstorm has no challenge surface today,
        // so this collapses to 'ranked' for all submitted runs. Wired
        // for forward-compat — the column accepts 'challenge' when the
        // feature ships.
        mode: "ranked",
      }),
    });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

/** v5(b): friendly wave name from the WAVE_CYCLE slug. The wave slugs
 *  are already lowercase-noun ("opening", "hailfront", "crossfire",
 *  "mortar", "the-eye"); we add minor titlecase and the wave index
 *  for context. */
function formatDiedInWave(slug: string, idx: number): string {
  if (!slug) return "";
  // Special-case the boss wave that uses a hyphenated slug.
  const pretty = slug === "the-eye" ? "The Eye" : slug.charAt(0).toUpperCase() + slug.slice(1);
  return `died in ${pretty} (wave ${idx + 1})`;
}

/** v5(c): render the dodge-cadence character — patient / quick /
 *  twitchy / frantic — based on mean_inter_dodge_ms. Suppresses when
 *  the field is null (legacy submission) or run too short to be
 *  meaningful (< 3 dodges, no real cadence pattern). Per
 *  docs/dossier-games/02-hailstorm/07-telemetry.md §3 Q8. */
function cadenceTag(mean_ms: number | null | undefined, total_dodges: number): string {
  if (mean_ms === null || mean_ms === undefined || total_dodges < 3) return "";
  if (mean_ms >= 2500) return `patient cadence (~${(mean_ms / 1000).toFixed(1)}s avg)`;
  if (mean_ms >= 1200) return `quick cadence (~${(mean_ms / 1000).toFixed(1)}s avg)`;
  if (mean_ms >= 600) return `twitchy cadence (~${mean_ms}ms avg)`;
  return `frantic cadence (~${mean_ms}ms avg)`;
}

/** v5(b): render the per-ability use tally if any ability fired. The
 *  loadout-aware emojis match the in-game HUD's slot icons. */
function formatAbilityUses(byKind: { dash: number; mine: number; slow: number } | undefined): string {
  if (!byKind) return "";
  const parts: string[] = [];
  if (byKind.dash > 0) parts.push(`${byKind.dash}↯ dash`);
  if (byKind.mine > 0) parts.push(`${byKind.mine}◇ mine`);
  if (byKind.slow > 0) parts.push(`${byKind.slow}⌖ slow`);
  return parts.join(" · ");
}

const canvas = document.getElementById("stage") as HTMLCanvasElement;
const hud = document.querySelector(".hud") as HTMLElement;
const hudDodges = document.getElementById("hud-dodges")!;
const hudTime = document.getElementById("hud-time")!;
const hudBest = document.getElementById("hud-best")!;
const waveBanner = document.getElementById("wave-banner") as HTMLElement;
const waveBannerName = document.getElementById("wave-banner-name") as HTMLElement;
const abilitySlots = [
  document.getElementById("ability-slot-0") as HTMLButtonElement,
  document.getElementById("ability-slot-1") as HTMLButtonElement,
];
const msgTitle = document.getElementById("msg-title")!;
const msgBody = document.getElementById("msg-body")!;
const btnStart = document.getElementById("btn-start") as HTMLButtonElement;
const btnReplay = document.getElementById("btn-replay") as HTMLButtonElement | null;
// v5(a): in-memory replay payload. The just-finished run's trace +
// dodge claims stay in module scope so btn-replay can re-feed them to
// the still-alive Hailstorm instance. Cleared on next startRun().
let lastReplayPayload: { trace: number[]; dodgeClaims: { idx: number; distance: number; t?: number }[] } | null = null;
const loadoutPicker = document.getElementById("loadout-picker") as HTMLElement;
const loadoutHint = document.getElementById("loadout-hint") as HTMLElement;
const submitForm = document.getElementById("submit-form") as HTMLFormElement;
const handleInputOverlay = document.getElementById("handle-input-overlay") as HTMLInputElement;
const handleError = document.getElementById("handle-error") as HTMLElement;
const btnSubmitScore = document.getElementById("btn-submit-score") as HTMLButtonElement;
const btnShare = document.getElementById("btn-share") as HTMLAnchorElement;

try {
  const pb = parseInt(localStorage.getItem(PB_KEY) || "0", 10) || 0;
  hudBest.textContent = pb.toLocaleString();
} catch {
  /* private mode */
}

// ---- Loadout selection (persisted) -----------------------------------
const ABILITY_DESC: Record<AbilityKind, string> = {
  dash: "0.7s i-frames · 5s cd",
  mine: "drop a 3s safe zone · 8s cd",
  slow: "reveal projectile paths · 2 charges",
};
let selectedLoadout: AbilityKind[] = readLoadout();

function readLoadout(): AbilityKind[] {
  try {
    const raw = localStorage.getItem(LOADOUT_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.every((k) => ALL_ABILITIES.includes(k))) {
        return (parsed as AbilityKind[]).slice(0, 2);
      }
    }
  } catch { /* swallow */ }
  return ["dash", "mine"];
}

function saveLoadout(l: AbilityKind[]) {
  try { localStorage.setItem(LOADOUT_KEY, JSON.stringify(l)); } catch { /* private mode */ }
}

function renderLoadoutPicker() {
  loadoutPicker.innerHTML = "";
  for (const k of ALL_ABILITIES) {
    const chip = document.createElement("button");
    chip.type = "button";
    chip.className = "loadout-chip";
    chip.dataset.kind = k;
    const selected = selectedLoadout.includes(k);
    if (selected) chip.classList.add("selected");
    chip.innerHTML = `<span class="loadout-name">${ABILITY_LABEL[k]}</span><span class="loadout-desc">${ABILITY_DESC[k]}</span>`;
    chip.addEventListener("click", () => {
      const isSelected = selectedLoadout.includes(k);
      if (isSelected) {
        selectedLoadout = selectedLoadout.filter((x) => x !== k);
      } else if (selectedLoadout.length < 2) {
        selectedLoadout = [...selectedLoadout, k];
      } else {
        // Replace the first slot.
        selectedLoadout = [selectedLoadout[1], k];
      }
      saveLoadout(selectedLoadout);
      renderLoadoutPicker();
      updateAbilitySlotLabels();
      loadoutHint.textContent = selectedLoadout.length === 2
        ? `Loadout: ${selectedLoadout.map((x) => ABILITY_LABEL[x]).join(" · ")}`
        : "Pick 2 abilities";
    });
    loadoutPicker.appendChild(chip);
  }
  loadoutHint.textContent = selectedLoadout.length === 2
    ? `Loadout: ${selectedLoadout.map((x) => ABILITY_LABEL[x]).join(" · ")}`
    : "Pick 2 abilities";
}

function updateAbilitySlotLabels() {
  for (let i = 0; i < 2; i++) {
    const slot = abilitySlots[i];
    if (!slot) continue;
    const kind = selectedLoadout[i];
    if (kind) {
      slot.classList.remove("empty");
      slot.dataset.kind = kind;
      const label = slot.querySelector(".ab-name");
      const key = slot.querySelector(".ab-key");
      if (label) label.textContent = ABILITY_LABEL[kind];
      if (key) key.textContent = i === 0 ? "Q" : "E";
    } else {
      slot.classList.add("empty");
      delete slot.dataset.kind;
    }
  }
}

renderLoadoutPicker();
updateAbilitySlotLabels();
// Ability slot click also triggers (in addition to Q/E keys).
for (let i = 0; i < 2; i++) {
  abilitySlots[i]?.addEventListener("click", () => {
    game?.tryUseAbility(i);
  });
}

// ---- Wave banner animation -------------------------------------------
let bannerTimeout = 0;
function showWaveBanner(name: string, boss: boolean) {
  waveBannerName.textContent = name;
  waveBanner.classList.toggle("boss", boss);
  waveBanner.classList.add("visible");
  window.clearTimeout(bannerTimeout);
  bannerTimeout = window.setTimeout(() => {
    waveBanner.classList.remove("visible");
  }, 1800);
}

// ---- Run lifecycle ---------------------------------------------------
let game: Hailstorm | null = null;
let activeRunId: string | null = null;
let activeSeed: number | null = null;
let pendingSubmit: {
  survivalS: number;
  score: number;
  dodges: number;
  dodgeClaims: { idx: number; distance: number; t?: number }[];
  trace: number[];
  abilitiesUsed: AbilityUse[];
  loadout: AbilityKind[];
  bossKills: { wave_idx: number }[];
} | null = null;

async function startRun() {
  if (selectedLoadout.length < 1) {
    loadoutHint.textContent = "Pick at least one ability";
    return;
  }
  if (game) game.armAudio();
  if (game) game.dispose();
  const dseed = getDailySeed();
  const runStart = dseed === null ? await startHailstormRun() : null;
  activeRunId = runStart?.run_id ?? null;
  const seed = dseed ?? runStart?.seed ?? Math.floor(Math.random() * 0x7fffffff);
  activeSeed = seed;

  game = new Hailstorm(canvas, {
    onHud: (state) => {
      hudTime.textContent = state.survivalS.toFixed(1);
      hudDodges.textContent = state.dodges.toLocaleString();
      // Update ability cooldown meters.
      for (let i = 0; i < 2; i++) {
        const slot = abilitySlots[i];
        if (!slot) continue;
        const kind = selectedLoadout[i];
        if (!kind) continue;
        if (kind === "slow") {
          slot.dataset.charges = String(state.slowCharges);
          slot.classList.toggle("ready", state.slowCharges > 0);
          const meter = slot.querySelector(".ab-meter") as HTMLElement | null;
          if (meter) meter.style.transform = `scaleX(${state.slowCharges > 0 ? 1 : 0})`;
        } else {
          const cd = state.cooldowns[kind];
          const total = kind === "dash" ? 5 : 8;
          const pct = Math.max(0, Math.min(1, 1 - cd / total));
          slot.classList.toggle("ready", cd <= 0.01);
          const meter = slot.querySelector(".ab-meter") as HTMLElement | null;
          if (meter) meter.style.transform = `scaleX(${pct})`;
        }
        slot.classList.toggle("active",
          (kind === "dash" && state.activeFx === "phase") ||
          (kind === "mine" && state.activeFx === "anchor") ||
          (kind === "slow" && state.activeFx === "foresight"));
      }
    },
    onWaveStart: (name, boss) => {
      showWaveBanner(name, boss);
    },
    onGameOver: async ({ survivalS, score, dodges, isNewBest, dodgeClaims, trace, abilitiesUsed, loadout, wavesCleared, bossesSurvived, bossKills }) => {
      hud.dataset.state = "over";
      msgTitle.textContent = bossKills.length > 0 ? "Bosses down" : bossesSurvived > 0 ? "Run ended" : "Hit";
      const newBestText = isNewBest ? " · new best" : "";
      const wavesText = wavesCleared > 0 ? ` · ${wavesCleared} waves` : "";
      const bossesText = bossesSurvived > 0 ? ` · ${bossesSurvived} bosses survived` : "";
      const killsText = bossKills.length > 0 ? ` · ${bossKills.length}💥 boss kills` : "";
      msgBody.textContent = `${survivalS.toFixed(1)}s · ${dodges} dodges${wavesText}${bossesText}${killsText} · ${score.toLocaleString()} pts${newBestText}`;
      btnStart.textContent = "Try again";
      // v5(a): expose in-memory replay. Trace must be at least one full
      // sample (3 floats); zero-length runs (instant hits) have nothing
      // worth playing back.
      lastReplayPayload = trace.length >= 6 ? { trace, dodgeClaims } : null;
      if (btnReplay) btnReplay.hidden = lastReplayPayload === null;
      try {
        const pb = parseInt(localStorage.getItem(PB_KEY) || "0", 10) || 0;
        hudBest.textContent = pb.toLocaleString();
      } catch { /* swallow */ }
      const xp = recordRun({ game: "hailstorm", score });
      showXpToast(xp.earnedXp, xp.totalXp, xp.rank);
      if (isDailyRun()) markDailyComplete("hailstorm", score);

      if (!activeRunId) return;
      const handle = localStorage.getItem(HANDLE_KEY) ?? "";
      if (handle) {
        const res = await finishHailstormRun(activeRunId, handle, survivalS, dodgeClaims, trace, abilitiesUsed, loadout, bossKills);
        if (res) renderShare(res, handle);
      } else {
        pendingSubmit = { survivalS, score, dodges, dodgeClaims, trace, abilitiesUsed, loadout, bossKills };
        submitForm.hidden = false;
        handleInputOverlay.value = "";
        const isCoarse = matchMedia("(pointer: coarse)").matches;
        if (!isCoarse) setTimeout(() => handleInputOverlay.focus({ preventScroll: true }), 80);
      }
    },
  }, seed);
  game.setLoadout(selectedLoadout);
  game.armAudio();
  try {
    const raw = localStorage.getItem("aether.settings");
    if (raw) {
      const s = JSON.parse(raw);
      if (typeof s.audioVolume === "number") game.setVolume(s.audioVolume);
      if (typeof s.muted === "boolean") game.setMuted(s.muted);
    }
  } catch { /* default volume/mute */ }
  game.start();
  hud.dataset.state = "running";
  msgTitle.textContent = "Hailstorm";
  msgBody.textContent = "";
}

btnStart.addEventListener("click", () => {
  // Reset replay state when a new run starts.
  lastReplayPayload = null;
  if (btnReplay) btnReplay.hidden = true;
  startRun();
});

if (btnReplay) {
  btnReplay.addEventListener("click", () => {
    if (!game || !lastReplayPayload) return;
    btnReplay.disabled = true;
    btnReplay.textContent = "Replaying…";
    hud.dataset.state = "replay";
    msgTitle.textContent = "Replay";
    msgBody.textContent = "Watch your run play back.";
    game.onReplayDone(() => {
      btnReplay.disabled = false;
      btnReplay.textContent = "Replay run";
      hud.dataset.state = "over";
      msgTitle.textContent = "Replay complete";
    });
    game.playReplay(lastReplayPayload);
  });
}

const settingsVolume = document.getElementById("settings-volume") as HTMLInputElement | null;
const settingsMuted = document.getElementById("settings-muted") as HTMLInputElement | null;
function readSharedSettings(): { audioVolume?: number; muted?: boolean } {
  try { return JSON.parse(localStorage.getItem("aether.settings") || "{}"); }
  catch { return {}; }
}
function patchSharedSettings(p: Record<string, unknown>) {
  const s = { ...readSharedSettings(), ...p };
  try { localStorage.setItem("aether.settings", JSON.stringify(s)); } catch { /* private mode */ }
}
const initial = readSharedSettings();
if (settingsVolume) {
  settingsVolume.value = String(initial.audioVolume ?? 0.7);
  settingsVolume.addEventListener("input", () => {
    const v = parseFloat(settingsVolume.value);
    patchSharedSettings({ audioVolume: v });
    game?.setVolume(v);
  });
}
if (settingsMuted) {
  settingsMuted.checked = !!initial.muted;
  settingsMuted.addEventListener("change", () => {
    patchSharedSettings({ muted: settingsMuted.checked });
    game?.setMuted(settingsMuted.checked);
  });
}

function renderShare(
  res: {
    rank: number;
    total: number;
    score: number;
    survival_s?: number;
    dodges_tight?: number;
    waves_cleared?: number;
    abilities_by_kind?: { dash: number; mine: number; slow: number };
    died_in_wave?: string;
    died_in_wave_idx?: number;
    // v5(c) cadence fields.
    mean_inter_dodge_ms?: number | null;
  },
  handle: string,
) {
  msgBody.textContent += ` · ranked #${res.rank} of ${res.total}`;
  // v5(b): per-dossier §6.2 "where you died" badge. Don't render when
  // the wave-cleared count zero-ish (player got hit right at start) —
  // the "opening" wave label adds no info for sub-second deaths.
  if (res.died_in_wave && res.survival_s !== undefined && res.survival_s > 5) {
    msgBody.textContent += ` · ${formatDiedInWave(res.died_in_wave, res.died_in_wave_idx ?? 0)}`;
  }
  // v5(b): per-kind ability tally — only when at least one ability fired.
  const abilityLine = formatAbilityUses(res.abilities_by_kind);
  if (abilityLine) msgBody.textContent += ` · used ${abilityLine}`;
  // v5(c): dodge-cadence character (patient / quick / twitchy / frantic).
  // Suppressed for legacy submissions (mean_inter_dodge_ms == null) and
  // too-short runs (< 3 dodges; no real pattern to characterize).
  const cadence = cadenceTag(res.mean_inter_dodge_ms, res.dodges_tight ?? 0);
  if (cadence) msgBody.textContent += ` · ${cadence}`;

  const params = new URLSearchParams({
    handle,
    score: String(res.score),
    rank: String(res.rank),
    game: "hailstorm",
  });
  if (res.survival_s !== undefined) params.set("survival_s", res.survival_s.toFixed(2));
  if (res.dodges_tight !== undefined) params.set("dodges_tight", String(res.dodges_tight));
  if (activeSeed !== null) params.set("seed", String(activeSeed));
  btnShare.href = `/share?${params.toString()}`;
  btnShare.hidden = false;
}

const HANDLE_RE = /^[A-Za-z0-9_-]{1,16}$/;

submitForm.addEventListener("submit", async (e) => {
  e.preventDefault();
  const value = handleInputOverlay.value.trim();
  if (!HANDLE_RE.test(value)) {
    handleError.hidden = false;
    return;
  }
  handleError.hidden = true;
  if (!pendingSubmit || !activeRunId) return;
  btnSubmitScore.disabled = true;
  btnSubmitScore.textContent = "Saving…";
  try { localStorage.setItem(HANDLE_KEY, value); } catch { /* private mode */ }
  const res = await finishHailstormRun(activeRunId, value, pendingSubmit.survivalS, pendingSubmit.dodgeClaims, pendingSubmit.trace, pendingSubmit.abilitiesUsed, pendingSubmit.loadout, pendingSubmit.bossKills);
  if (res) {
    renderShare(res, value);
    btnSubmitScore.textContent = "Saved";
  } else {
    btnSubmitScore.disabled = false;
    btnSubmitScore.textContent = "Save score";
    handleError.textContent = "Submission failed — try again.";
    handleError.hidden = false;
  }
  submitForm.hidden = true;
  pendingSubmit = null;
});

handleInputOverlay.addEventListener("input", () => {
  if (handleError.hidden) return;
  if (HANDLE_RE.test(handleInputOverlay.value.trim())) handleError.hidden = true;
});

window.addEventListener("keydown", (e) => {
  if (e.target instanceof HTMLInputElement) return;
  if (e.code === "Enter" && hud.dataset.state !== "running") {
    startRun();
  }
});
